import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * ROC Motion System — primitives reutilizables.
 * Reglas: entradas 450–700ms, desplazamientos 16–32px, stagger 50–100ms.
 * Todo respeta prefers-reduced-motion (el contenido siempre queda visible).
 */

export const EASE = [0.22, 0.61, 0.36, 1] as const;

type Direction = "up" | "down" | "left" | "right" | "none";

const offsetFor = (direction: Direction, distance: number) => {
  switch (direction) {
    case "up":
      return { y: distance };
    case "down":
      return { y: -distance };
    case "left":
      return { x: distance };
    case "right":
      return { x: -distance };
    default:
      return {};
  }
};

interface RevealProps extends Omit<HTMLMotionProps<"div">, "children"> {
  children: ReactNode;
  direction?: Direction;
  distance?: number;
  delay?: number;
  duration?: number;
  once?: boolean;
  amount?: number;
}

/** Bloque que aparece al entrar en viewport. */
export const RevealBlock = forwardRef<HTMLDivElement, RevealProps>(
  (
    {
      children,
      direction = "up",
      distance = 24,
      delay = 0,
      duration = 0.6,
      once = true,
      amount = 0.25,
      className,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();

    if (reduced) {
      return (
        <div ref={ref} className={className}>
          {children}
        </div>
      );
    }

    return (
      <motion.div
        ref={ref}
        className={className}
        initial={{ opacity: 0, ...offsetFor(direction, distance) }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once, amount }}
        transition={{ duration, delay, ease: EASE }}
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);
RevealBlock.displayName = "RevealBlock";

interface StaggerGroupProps {
  children: ReactNode;
  className?: string;
  stagger?: number;
  delay?: number;
  once?: boolean;
  as?: "div" | "ul" | "ol";
}

/** Contenedor que escalona la entrada de sus StaggerItem hijos. */
export const StaggerGroup = ({
  children,
  className,
  stagger = 0.07,
  delay = 0,
  once = true,
  as = "div",
}: StaggerGroupProps) => {
  const reduced = useReducedMotion();
  const Comp = motion[as];

  if (reduced) {
    const Plain = as;
    return <Plain className={className}>{children}</Plain>;
  }

  return (
    <Comp
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.2 }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {children}
    </Comp>
  );
};

interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  distance?: number;
  as?: "div" | "li";
}

export const StaggerItem = ({ children, className, distance = 18, as = "div" }: StaggerItemProps) => {
  const reduced = useReducedMotion();
  const Comp = motion[as];

  if (reduced) {
    const Plain = as;
    return <Plain className={className}>{children}</Plain>;
  }

  return (
    <Comp
      className={className}
      variants={{
        hidden: { opacity: 0, y: distance },
        visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
      }}
    >
      {children}
    </Comp>
  );
};

interface RevealTextProps {
  text: string;
  className?: string;
  lineClassName?: string;
  delay?: number;
  /** Divide por palabras para revelar en grupos, manteniendo el texto real en el DOM. */
  as?: "h1" | "h2" | "p" | "span";
}

/**
 * Revela un texto por palabras. El texto completo permanece en el HTML
 * (crawlable) y visible de inmediato con reduced-motion.
 */
export const RevealText = ({ text, className, delay = 0, as = "h1" }: RevealTextProps) => {
  const reduced = useReducedMotion();
  const Tag = as;

  if (reduced) {
    return <Tag className={className}>{text}</Tag>;
  }

  const MotionTag = motion[as];
  const words = text.split(" ");

  return (
    <MotionTag
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.045, delayChildren: delay } },
      }}
    >
      {words.map((word, i) => (
        <span key={`${word}-${i}`} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: "0.9em", opacity: 0 },
              visible: { y: 0, opacity: 1, transition: { duration: 0.65, ease: EASE } },
            }}
          >
            {word}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
};

interface ImageRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

/** Reveal por máscara vertical para fotografía editorial. */
export const ImageReveal = ({ children, className, delay = 0 }: ImageRevealProps) => {
  const reduced = useReducedMotion();

  if (reduced) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={cn("overflow-hidden", className)}
      initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.4 }}
      whileInView={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

/** Dibuja un path SVG cuando entra en viewport. */
interface DrawPathProps {
  d: string;
  className?: string;
  delay?: number;
  duration?: number;
  strokeWidth?: number;
  once?: boolean;
}

export const DrawPath = ({
  d,
  className,
  delay = 0,
  duration = 1.4,
  strokeWidth = 1,
  once = true,
}: DrawPathProps) => {
  const reduced = useReducedMotion();

  if (reduced) {
    return <path d={d} className={className} strokeWidth={strokeWidth} fill="none" />;
  }

  return (
    <motion.path
      d={d}
      className={className}
      strokeWidth={strokeWidth}
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      whileInView={{ pathLength: 1, opacity: 1 }}
      viewport={{ once, amount: 0.4 }}
      transition={{ pathLength: { duration, ease: EASE, delay }, opacity: { duration: 0.3, delay } }}
    />
  );
};
